import type { WebEDIRowData } from 'support/types/webediTable';

import { hasSscc } from './generateSscc';
import { lineItemRef, refIndex, transportUnitRef } from './packingListRefs';

export { hasSscc };

/**
 * A transport-unit row is a "placeholder" (the canonical default unit, not a real described unit)
 * when it carries no SSCC/`unit_identifier`. Once the packing-list modal generates identifiers on
 * apply, real units always have one, so this predicate only matches the default scaffolding.
 */
export const isPlaceholderTransportUnit = (row: WebEDIRowData | undefined): boolean => !hasSscc(row?.unit_identifier);

export const isLineItemInPlaceholderUnit = (
  row: WebEDIRowData | undefined,
  transportUnits: Array<WebEDIRowData>,
): boolean => {
  const parentIndex = refIndex(row?._internal?.parent_transport_unit);
  if (parentIndex === null) {
    return true;
  }
  return isPlaceholderTransportUnit(transportUnits[parentIndex]);
};

type PackingData = {
  line_items: Array<WebEDIRowData>;
  transport_units: Array<WebEDIRowData>;
};

export type PackingListOriginalIndexes = {
  line_items: Array<number>;
  transport_units: Array<number>;
};

/**
 * Removes the default placeholder transport units (no SSCC) and every line item that sits in one
 * (including unplaced items and their nested children), then re-derives all index-based
 * `parent_transport_unit` / `parent_line_item` refs against the compacted arrays.
 *
 * This is the single source of truth applied at BOTH validation and submit, so the payload we
 * validate is exactly the payload we send. It is a pure no-op when there are no placeholder units
 * (e.g. a document that already arrived with real, SSCC-bearing structure).
 */
export const stripPlaceholderPackingData = ({ line_items, transport_units }: PackingData): PackingData => {
  return stripPlaceholderPackingDataWithIndexes({ line_items, transport_units }).data;
};

export const stripDefaultTransportUnitFromValuesWithIndexes = <T extends Record<string, unknown>>(
  values: T,
  packingListEnabled = false,
): { values: T; originalIndexes?: PackingListOriginalIndexes } => {
  if (!packingListEnabled || !Array.isArray(values.line_items) || !Array.isArray(values.transport_units)) {
    return { values };
  }
  const { data, originalIndexes } = stripPlaceholderPackingDataWithIndexes({
    line_items: values.line_items as Array<WebEDIRowData>,
    transport_units: values.transport_units as Array<WebEDIRowData>,
  });
  return { values: { ...values, ...data }, originalIndexes };
};

/**
 * Applies {@link stripPlaceholderPackingData} to the `line_items`/`transport_units` of a flat form
 * values object, leaving all other keys untouched. A pure no-op when `packingListEnabled` is false
 * or the arrays are absent — safe to call unconditionally at every validate/submit chokepoint.
 *
 * Running the SAME transform at validation and at submit guarantees we validate exactly the payload
 * we send: the default placeholder unit and its unplaced items never reach datachecks or the API.
 */
export const stripDefaultTransportUnitFromValues = <T extends Record<string, unknown>>(
  values: T,
  packingListEnabled = false,
): T => stripDefaultTransportUnitFromValuesWithIndexes(values, packingListEnabled).values;

const stripPlaceholderPackingDataWithIndexes = ({
  line_items,
  transport_units,
}: PackingData): { data: PackingData; originalIndexes: PackingListOriginalIndexes } => {
  const keptTransportUnitOldIndices: Array<number> = [];
  const newTuIndexByOld = new Map<number, number>();
  const strippedTransportUnits: Array<WebEDIRowData> = [];
  transport_units.forEach((row, oldIndex) => {
    if (isPlaceholderTransportUnit(row)) {
      return;
    }
    keptTransportUnitOldIndices.push(oldIndex);
    newTuIndexByOld.set(oldIndex, strippedTransportUnits.length);
    strippedTransportUnits.push(row);
  });

  const remapTransportUnitRef = (ref: string | undefined): string => {
    const oldIndex = refIndex(ref);
    const newIndex = oldIndex === null ? undefined : newTuIndexByOld.get(oldIndex);
    return newIndex === undefined ? '' : transportUnitRef(newIndex);
  };

  transport_units.forEach((row, oldIndex) => {
    const newIndex = newTuIndexByOld.get(oldIndex);
    if (newIndex === undefined) {
      return;
    }
    strippedTransportUnits[newIndex] = {
      ...row,
      _internal: {
        ...row._internal,
        parent_transport_unit: remapTransportUnitRef(row._internal?.parent_transport_unit),
      },
    };
  });

  const keptLineItemOldIndices: Array<number> = [];
  line_items.forEach((row, oldIndex) => {
    if (!isLineItemInPlaceholderUnit(row, transport_units)) {
      keptLineItemOldIndices.push(oldIndex);
    }
  });
  const newLiIndexByOld = new Map<number, number>();
  keptLineItemOldIndices.forEach((oldIndex, newIndex) => newLiIndexByOld.set(oldIndex, newIndex));

  const remapLineItemRef = (ref: string | undefined): string => {
    const oldIndex = refIndex(ref);
    const newIndex = oldIndex === null ? undefined : newLiIndexByOld.get(oldIndex);
    return newIndex === undefined ? '' : lineItemRef(newIndex);
  };

  const strippedLineItems = keptLineItemOldIndices.map((oldIndex) => {
    const row = line_items[oldIndex];
    return {
      ...row,
      _internal: {
        ...row._internal,
        parent_transport_unit: remapTransportUnitRef(row._internal?.parent_transport_unit),
        parent_line_item: remapLineItemRef(row._internal?.parent_line_item),
      },
    };
  });

  return {
    data: { line_items: strippedLineItems, transport_units: strippedTransportUnits },
    originalIndexes: {
      line_items: keptLineItemOldIndices,
      transport_units: keptTransportUnitOldIndices,
    },
  };
};
